import {
  Create,
  CreateButton,
  Datagrid,
  Edit,
  EditButton,
  FileField,
  FileInput,
  FunctionField,
  List,
  NumberField,
  required,
  SimpleForm,
  TextField,
  TextInput,
  useNotify,
  Link,
  BooleanField,
  BooleanInput,
  useListContext,
  useUpdateMany,
  useRefresh,
  useUnselectAll,
  Button,
  useRecordContext,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Fragment, useState } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import SyncIcon from '@mui/icons-material/Sync';
import { useClerk } from '@clerk/clerk-react';
import axios from 'axios';

const NEXT_PUBLIC_CSAPI_BASE_URL = process.env.NEXT_PUBLIC_CSAPI_BASE_URL;
import { npiNotification } from '../utility/target-list-upload/npi-notification';
import { DataTableListActions } from './data-table-list-actions';
import { FormHeader, FormSectionHeader } from './forms/form-components';
import { ESTDateField } from './custom-ra-fields/est-date-field';
import { includesInsensitive } from './forms/form-utils';

const targetListFilters: JSX.Element[] = [
  <TextInput label="Target List Name" source="name" {...includesInsensitive} alwaysOn />,
  <TextInput label="Target List Description" source="description" {...includesInsensitive} alwaysOn />,
  <BooleanInput label="Include Archived" source="includeArchived" alwaysOn />,
];

const BulkArchiveButton = () => {
  const { selectedIds } = useListContext();
  const [updateMany, { isLoading }] = useUpdateMany();
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll('targetLists');

  const handleClick = () => {
    updateMany(
      'targetLists',
      { ids: selectedIds, data: { isArchived: true } },
      {
        onSuccess: () => {
          notify(`${selectedIds.length} target list(s) archived`, { type: 'success' });
          unselectAll();
          refresh();
        },
        onError: () => {
          notify('Error archiving target lists', { type: 'error' });
        },
      }
    );
  };

  return (
    <Button label="Archive" onClick={handleClick} disabled={isLoading}>
      <ArchiveIcon />
    </Button>
  );
};

const BulkUnarchiveButton = () => {
  const { selectedIds } = useListContext();
  const [updateMany, { isLoading }] = useUpdateMany();
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll('targetLists');

  const handleClick = () => {
    updateMany(
      'targetLists',
      { ids: selectedIds, data: { isArchived: false } },
      {
        onSuccess: () => {
          notify(`${selectedIds.length} target list(s) unarchived`, { type: 'success' });
          unselectAll();
          refresh();
        },
        onError: () => {
          notify('Error unarchiving target lists', { type: 'error' });
        },
      }
    );
  };

  return (
    <Button label="Unarchive" onClick={handleClick} disabled={isLoading}>
      <UnarchiveIcon />
    </Button>
  );
};

const TargetListBulkActions = () => (
  <>
    <BulkArchiveButton />
    <BulkUnarchiveButton />
  </>
);

export type TargetListLineItemRef = {
  placementsLineItemId?: string | null;
  lineItemId?: string | null;
  lineItemNumber?: number | null;
};

// MD-5164 / MD-5186: always prefer the native lineItems route when lineItemId is
// present (PIO API is dead — legacy /line_items show only redirects). Imported
// attaches that still lack a native UUID keep the legacy path, which Phase A
// redirects to native. Display: #<lineItemNumber> when available, else placements
// id or native uuid.
export const targetListLineItemRefLink = (
  ref: TargetListLineItemRef | null | undefined,
): { label: string; to: string } | undefined => {
  if (ref?.lineItemId) {
    return {
      label:
        ref.lineItemNumber != null
          ? `#${ref.lineItemNumber}`
          : ref.placementsLineItemId ?? ref.lineItemId,
      to: `/lineItems/${ref.lineItemId}/show`,
    };
  }
  if (ref?.placementsLineItemId) {
    return {
      label: ref.placementsLineItemId,
      // Temporary: legacy resource redirects to native (MD-5186 Phase A).
      to: `/line_items/${ref.placementsLineItemId}/show`,
    };
  }
  return undefined;
};

// MD-5164: while the creative tables' native line_item_id is only partially
// backfilled, two creatives on the same target list can resolve to the same line
// item (one backfilled, one not). The *_line_item_refs functions DISTINCT on the
// full jsonb, so those rows no longer collapse — dedupe by route here so each
// line item renders once (the pre-refs behavior) and the React keys stay unique.
export const dedupeTargetListLineItemLinks = (
  links: { label: string; to: string }[],
): { label: string; to: string }[] => Array.from(new Map(links.map(link => [link.to, link])).values());

const LineItemLinks = ({ refs }: { refs?: (TargetListLineItemRef | null)[] | null }) => {
  const links = dedupeTargetListLineItemLinks((refs ?? []).flatMap(ref => targetListLineItemRefLink(ref) ?? []));
  if (!links.length) return null;

  return (
    <>
      {links.map((link, index) => (
        <Fragment key={link.to}>
          <Link to={link.to}>{link.label}</Link>
          {index < links.length - 1 ? ', ' : ''}
        </Fragment>
      ))}
    </>
  );
};

export const formatTargetListLineItemIds = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join('\n');
  }

  return value == null ? '' : String(value);
};

const TargetListLineItemsInput = () => {
  const record = useRecordContext();

  return (
    <>
      <FormSectionHeader>Line Item Coverage</FormSectionHeader>
      <TextInput
        source="targetListLineItemIdsText"
        label="Line Item IDs"
        multiline
        fullWidth
        defaultValue={formatTargetListLineItemIds(record?.placementLineItemIdsList)}
        helperText="Enter one legacy PIO line-item ID or native line-item UUID per line."
      />
    </>
  );
};

const TargetListsListActions = () => (
  <DataTableListActions filters={targetListFilters}>
    <CreateButton label="Upload" />
  </DataTableListActions>
);

export const TargetListsList = () => (
  <List
    actions={<TargetListsListActions />}
    perPage={25}
    filterDefaultValues={{ includeArchived: false }}
  >
    <Datagrid bulkActionButtons={<TargetListBulkActions />}>
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="description" />
      <FunctionField
        render={(record: any) => (record.npiCount ? Number(record.npiCount).toLocaleString() : '')}
        label="NPI Count"
        textAlign="center"
      />
      <FunctionField
        render={(record: any) => <LineItemLinks refs={record.bannerLineItemRefsList} />}
        label="Banner Line Items"
      />

      <FunctionField
        render={(record: any) => <LineItemLinks refs={record.clicksLineItemRefsList} />}
        label="Clicks Line Items"
      />

      <FunctionField
        render={(record: any) => <LineItemLinks refs={record.fmaLineItemRefsList} />}
        label="FMA Line Items"
      />

      <ESTDateField source="updatedAt" label="Updated At" />
      <BooleanField source="isArchived" label="Archived" />
      <EditButton />
    </Datagrid>
  </List>
);

export const TargetListCreate = () => {
  const notify = useNotify();
  const [isSegmented, setIsSegmented] = useState(false);

  return (
    <Create>
      <SimpleForm>
        <FormHeader>Create Target List</FormHeader>
        <TextInput source="name" />
        <TextInput multiline fullWidth source="description" />
        <BooleanInput
          source="isSegmented"
          label="Split by Segment"
          helperText="Create multiple target lists, one for each unique segment value in the file"
          onChange={(e: any) => setIsSegmented(e.target.checked)}
        />
        <FileInput
          source="targetListFile"
          label="Target List File"
          accept=".txt, .csv"
          validate={required()}
          helperText={
            isSegmented
              ? "Segmented mode: File must have exactly 2 columns with headers 'NPI' and 'Segment' (case insensitive). One target list will be created for each unique segment value, named '{your_name}__{segment}'. Files should be in tab- or comma-separated values format (.txt or .csv)."
              : "Files should be in tab- or comma-separated values format (.txt or .csv). Please indicate which column contains NPI numbers by including 'NPI' in first (header) row."
          }
          options={{ onDrop: file => npiNotification(file[0], notify) }}
        >
          <FileField source="src" title="title" />
        </FileInput>
      </SimpleForm>
    </Create>
  );
};

const SyncToIterableButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const { session } = useClerk();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (!record?.id) {
      notify('Target list ID not found', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const authToken = await session?.getToken();
      await axios.post(
        `${NEXT_PUBLIC_CSAPI_BASE_URL}/api/iterable-sync/${record.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      notify('Iterable sync initiated successfully', { type: 'success' });
    } catch (error) {
      console.error('Error initiating Iterable sync:', error);
      notify('Error initiating Iterable sync', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button label="Sync Acquisition Contacts to Iterable" onClick={handleClick} disabled={isLoading}>
      <SyncIcon />
    </Button>
  );
};

const TargetListEditToolbar = () => (
  <Toolbar>
    <SaveButton />
    <SyncToIterableButton />
  </Toolbar>
);

export const TargetListEdit = () => {
  const notify = useNotify();
  return (
    <Edit mutationMode="pessimistic">
      <SimpleForm toolbar={<TargetListEditToolbar />}>
        <FormHeader>Update Target List</FormHeader>
        <TextInput source="name" />
        <TextInput multiline fullWidth source="description" />
        <TextField source="uploadedFilename" label="Last Uploaded Filename" />
        <ESTDateField source="uploadedAt" label="Last Uploaded At" />
        <NumberField source="npiCount" label="NPI Count" />
        <TargetListLineItemsInput />
        <FileInput
          source="targetListFile"
          label="Target List File"
          accept=".txt, .csv"
          helperText="Files should be in tab- or comma-separated values format (.txt or .csv). Please indicate which column contains NPI numbers by including 'NPI' in first (header) row."
          options={{ onDrop: file => npiNotification(file[0], notify) }}
        >
          <FileField source="src" title="title" />
        </FileInput>
      </SimpleForm>
    </Edit>
  );
};
